import { ReactNode, useContext, useEffect } from 'react'
import TestimonialPlusSignup from '@/checkout/components/TestimonialPlusSignup'
import FullPageLoader from '@/components/loaders/FullPageLoader'
import { AuthContext } from '@/context/auth-context'
import OrgAccessDeniedPage from './OrgAccessDeniedPage'
import OrgDataUnavailableHold from './OrgDataUnavailableHold'
import TestimonialPlusResignup from '@/checkout/components/TestimonialPlusResignup'
import { useFetchTrialDaysQuery, useFetchCheckoutPricesQuery } from '@/checkout/store/slice'

/**
 * Subscription Guard
 * Guards the app from use by users who have not subscribed after their trial period.
 */
type SubscriptionGuardProps = {
  children: ReactNode
}

export default function SubscriptionGuard(props: SubscriptionGuardProps) {
  const { children } = props
  const { user, userAccountData } = useContext(AuthContext)
  const permanentUser = user != null && !user.isAnonymous
  const { isLoading: isLoadingTrialDays, refetch: refetchTrialDays } = useFetchTrialDaysQuery(undefined, {
    skip: !permanentUser,
  })
  const { isLoading: isLoadingCheckoutPrices, refetch: refetchCheckoutPrices } = useFetchCheckoutPricesQuery(undefined, {
    skip: !permanentUser,
  })

  useEffect(() => {
    if (permanentUser) {
      refetchTrialDays()
      refetchCheckoutPrices()
    }
  }, [permanentUser, refetchTrialDays, refetchCheckoutPrices, userAccountData])

  // Conditional Rendering Based On User And Subscription Status
  switch (true) {
    // Anonymous Users - No Guard
    case user?.isAnonymous == true:
      return children

    // Org member whose organization data could not be loaded (transient getOrganization() miss).
    // Must come before validSubscription: with orgData missing, the subscription getters fall back
    // to the member's individual JWT claim, which can read as a valid trial/paid subscription and
    // admit them past an expired org. Hold instead; OrgDataUnavailableHold retries and resolves to
    // real access or OrgAccessDeniedPage, or surfaces an error on a sustained outage.
    case userAccountData?.orgDataUnavailable:
      return <OrgDataUnavailableHold />

    // Valid Subscription - No Guard
    case userAccountData?.validSubscription:
      return children

    case isLoadingTrialDays || isLoadingCheckoutPrices:
      return <FullPageLoader />

    // Pending Subscription - Show Loader
    case userAccountData?.pendingSubscription:
      return <FullPageLoader />

    // Invalid Subscription - Organization
    // Must come before the generic individual case below, which would otherwise shadow it and
    // route expired-org members to the individual paywall instead of OrgAccessDeniedPage.
    case userAccountData?.invalidSubscription && userAccountData?.isOrgAccount:
      return <OrgAccessDeniedPage />

    // Invalid Subscription - Individual - Show Paywall
    case userAccountData?.invalidSubscription:
      return <TestimonialPlusResignup />

    // Pending Payment
    case userAccountData?.pendingPaymentMethod:
      return <TestimonialPlusSignup />

    // null user
    case !user:
      return <FullPageLoader />

    // All other cases
    default:
      console.error('Invalid rendering condition for subscription guard: ', user, userAccountData)
      return children
  }
}
